/**
 * LiveActivityTicker — single rotating line of recent prom activity.
 *
 * Polls /api/events/<id>/activity every 8s. Cycles through the latest
 * messages with a fade so the page feels alive even when nothing is
 * happening this second. Falls back to "Be the first 🔥" when the
 * feed is empty (real activity only — seeds are excluded server-side).
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Flame } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 8000;
const ROTATE_MS = 4000;

export default function LiveActivityTicker({ eventId, fallback = 'Be the first 🔥' }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!eventId) return undefined;
    let pollTimer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}/activity`);
        if (!mountedRef.current) return;
        setItems(res?.data?.items || []);
      } catch (e) { /* silent */ }
      finally {
        if (mountedRef.current) pollTimer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { if (pollTimer) clearTimeout(pollTimer); };
  }, [eventId]);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  const current = items[idx]?.label || fallback;

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-300/10 border border-amber-300/25 text-amber-200 text-[12px]" data-testid="live-activity-ticker">
      <Flame size={12} weight="fill" />
      <AnimatePresence mode="wait">
        <motion.span
          key={current + idx}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3 }}
          className="font-bold"
        >
          {current}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}
